// Centered infinite-peek carousel with pagination dots and an active-slide
// info panel (title + shop button) that lives outside the track and updates
// as slides change. Isolated from the theme's shared slideshow-component.
class SignatureCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-sl-viewport]');
    this.track = this.querySelector('[data-sl-track]');
    if (!this.viewport || !this.track) return;

    this.originals = Array.from(this.track.children);
    this.realCount = this.originals.length;
    if (this.realCount === 0) return;

    this.slidesData = this.originals.map((el) => ({
      title: el.dataset.slTitle || '',
      button: el.dataset.slButton || '',
      link: el.dataset.slLink || '',
    }));

    this.info = this.querySelector('[data-sl-info]');
    this.infoTitle = this.querySelector('[data-sl-info-title]');
    this.infoButton = this.querySelector('[data-sl-info-button]');
    this.infoButtonLabel = this.querySelector('[data-sl-info-button-label]');
    this.dots = Array.from(this.querySelectorAll('[data-sl-dot]'));

    if (this.realCount === 1) {
      this.index = 0;
      this.#updateInfo(0);
      this.#markActive(0);
      return;
    }

    this.index = this.realCount; // first original sits at position [realCount]
    this.slideStep = 0;
    this.slideOnly = 0;
    this.animating = false;
    this.autoplayMs = parseInt(this.getAttribute('data-autoplay') || '0', 10);
    this.autoplayTimer = null;

    this.#buildClones();
    this.#measure();
    this.#positionInstant();
    this.#markActive(this.index);
    this.#updateInfo(this.#realIndex());

    this.prevBtn = this.querySelector('[data-sl-prev]');
    this.nextBtn = this.querySelector('[data-sl-next]');
    this.prevBtn?.addEventListener('click', () => this.move(-1));
    this.nextBtn?.addEventListener('click', () => this.move(1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = parseInt(dot.dataset.slIndex || '0', 10);
        this.goto(target);
      });
    });

    this.track.addEventListener('transitionend', this.#onTransitionEnd);
    this.#setupDrag();
    this.#setupWheel();
    this.#setupVideos();

    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#positionInstant();
    });
    this.#resizeObserver.observe(this.viewport);

    if (this.autoplayMs > 0) {
      this.addEventListener('pointerenter', this.#stopAutoplay);
      this.addEventListener('pointerleave', this.#startAutoplay);
      this.#startAutoplay();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#stopAutoplay();
  }

  #buildClones() {
    // [clones of originals][originals][clones of originals]
    // Insert before-clones in order before the first original so positions are:
    // 0..N-1 = clones in original order, N..2N-1 = originals, 2N..3N-1 = clones again.
    const firstOriginal = this.originals[0];
    this.originals.forEach((el) => {
      this.track.insertBefore(this.#clone(el), firstOriginal);
    });
    this.originals.forEach((el) => {
      this.track.appendChild(this.#clone(el));
    });
  }

  #clone(el) {
    const c = el.cloneNode(true);
    c.setAttribute('data-sl-clone', '');
    c.querySelectorAll('video').forEach((v) => {
      v.muted = true;
      const playAttempt = v.play();
      if (playAttempt?.catch) playAttempt.catch(() => {});
    });
    return c;
  }

  #measure() {
    const first = this.track.children[0];
    if (!first) return;
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    this.slideOnly = first.offsetWidth;
    this.slideStep = first.offsetWidth + gap;
  }

  get #centerOffset() {
    const viewportWidth = this.viewport.offsetWidth;
    return viewportWidth / 2 - (this.index * this.slideStep + this.slideOnly / 2);
  }

  #positionInstant() {
    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${this.#centerOffset}px,0,0)`;
    void this.track.offsetWidth;
    this.track.style.transition = '';
  }

  #positionAnimated() {
    this.track.style.transform = `translate3d(${this.#centerOffset}px,0,0)`;
  }

  #realIndex() {
    return ((this.index - this.realCount) % this.realCount + this.realCount) % this.realCount;
  }

  move(dir) {
    if (this.animating || this.realCount <= 1) return;
    this.animating = true;
    this.index += dir;
    this.#positionAnimated();
    this.#markActive(this.index);
    this.#updateInfo(this.#realIndex());

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#settle(), 650);
  }

  goto(realIndex) {
    if (this.animating || this.realCount <= 1) return;
    const target = this.realCount + realIndex;
    if (target === this.index) return;
    this.animating = true;
    this.index = target;
    this.#positionAnimated();
    this.#markActive(this.index);
    this.#updateInfo(realIndex);

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#settle(), 650);
  }

  #onTransitionEnd = (e) => {
    if (e.target !== this.track || e.propertyName !== 'transform') return;
    this.#settle();
  };

  #settle() {
    if (!this.animating) return;
    clearTimeout(this.#settleTimer);
    if (this.index >= this.realCount * 2) this.index -= this.realCount;
    else if (this.index < this.realCount) this.index += this.realCount;
    this.#positionInstant();
    this.#markActive(this.index);
    this.animating = false;
  }

  #settleTimer;

  #markActive(activeIdx) {
    const children = this.track.children;
    for (let i = 0; i < children.length; i++) {
      children[i].classList.toggle('is-active', i === activeIdx);
    }
    const real = this.#realIndex();
    this.dots.forEach((dot, i) => {
      const isActive = i === real;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  #updateInfo(realIndex) {
    if (!this.info) return;
    const data = this.slidesData[realIndex] || { title: '', button: '', link: '' };
    if (this.infoTitle) this.infoTitle.textContent = data.title;
    if (this.infoButton && this.infoButtonLabel) {
      if (data.button && data.link) {
        this.infoButtonLabel.textContent = data.button;
        this.infoButton.setAttribute('href', data.link);
        this.infoButton.hidden = false;
      } else {
        this.infoButton.hidden = true;
        this.infoButtonLabel.textContent = '';
      }
    }
  }

  #setupDrag() {
    let startX = 0;
    let dragging = false;
    const threshold = 40;

    this.viewport.addEventListener('pointerdown', (e) => {
      dragging = true;
      startX = e.clientX;
    });
    this.viewport.addEventListener('pointerup', (e) => {
      if (!dragging) return;
      dragging = false;
      const delta = e.clientX - startX;
      if (Math.abs(delta) > threshold) this.move(delta < 0 ? 1 : -1);
    });
    this.viewport.addEventListener('pointercancel', () => {
      dragging = false;
    });
  }

  #setupWheel() {
    let wheelLock = false;
    this.viewport.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        if (Math.abs(e.deltaX) < 8) return;
        e.preventDefault();
        if (wheelLock) return;
        wheelLock = true;
        this.move(e.deltaX > 0 ? 1 : -1);
        setTimeout(() => {
          wheelLock = false;
        }, 450);
      },
      { passive: false }
    );
  }

  #setupVideos() {
    // Hide all native browser overlay icons (PiP, remote-playback, skip,
    // fullscreen, download) on every <video> in the carousel — originals + clones.
    this.track.querySelectorAll('video').forEach((v) => {
      v.disablePictureInPicture = true;
      v.setAttribute('disablepictureinpicture', '');
      v.setAttribute('disableremoteplayback', '');
      v.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
      v.removeAttribute('controls');
    });

    // If a slide is set to play/pause on click, wire that up. Clicking the
    // video toggles play/pause and the click doesn't navigate anywhere.
    this.track.querySelectorAll('.sl-slide--video-pause').forEach((slide) => {
      const video = slide.querySelector('video');
      if (!video) return;
      slide.addEventListener('click', (e) => {
        // Don't fight the carousel's drag handler — if the user actually dragged,
        // the carousel's click prevention will have already kicked in.
        if (e.defaultPrevented) return;
        e.preventDefault();
        if (video.paused) {
          const r = video.play();
          if (r && r.catch) r.catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  #startAutoplay = () => {
    if (this.autoplayMs <= 0 || this.autoplayTimer) return;
    this.autoplayTimer = setInterval(() => {
      if (document.hidden) return;
      this.move(1);
    }, this.autoplayMs);
  };

  #stopAutoplay = () => {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  };

  #resizeObserver;
}

if (!customElements.get('signature-carousel')) {
  customElements.define('signature-carousel', SignatureCarousel);
}
